import React, { FC, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Flex } from '@rover/kibble/core';

import { FeedbackMessage, PrimaryLabel, SecondaryLabel } from '../sharedTypography';

import { SelectFieldCommonProps, SelectFieldOptionConfig } from './SelectField.common';
import SelectFieldInput from './SelectFieldInput';
import SelectFieldOptions, { SelectFieldOptionsHandle } from './SelectFieldOptions';

/** A dropdown form field for exactly one answer from a list of options. */
const SelectField: FC<SelectFieldCommonProps> = ({
  options,
  placeholder,
  required = true,
  disabled,
  primaryLabel,
  primaryLabelA11yHidden,
  primaryLabelSize,
  secondaryLabel,
  fieldError,
  helperMessage,
  requirednessLabel,
  control,
  name,
  onChange = () => {},
  inputWidth,
  variantOptions = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<SelectFieldOptionsHandle>(null);
  const selectFieldRef = useRef<HTMLDivElement>(null);

  const primaryLabelId = useId();
  const secondaryLabelId = useId();
  const inputId = useId();
  const menuId = useId();
  const fieldErrorId = useId();
  const helperMessageId = useId();

  const { field } = useController({ control, defaultValue: null, name });

  const selectedOption = options.find((option) => option.value === field.value) || null;

  const toggleOpen = useCallback((): void => setIsOpen(!isOpen), [isOpen]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    switch (event.key) {
      case KEYS.DOWN:
      case KEYS.RIGHT:
        event.preventDefault();
        setIsOpen(true);
        menuRef.current?.handleOptionFocus(0);
        break;

      case KEYS.UP:
      case KEYS.LEFT:
        event.preventDefault();
        setIsOpen(true);
        menuRef.current?.handleOptionFocus(options.length - 1);
        break;

      case KEYS.SPACE:
      case KEYS.ENTER:
        event.preventDefault();
        toggleOpen();
        menuRef.current?.handleOptionFocus();
        break;

      case KEYS.TAB:
        // Don't prevent the default behavior for tab key
        break;

      default:
        if (isOpen) {
          event.preventDefault();
          menuRef.current?.handleKeyDown(event);
        }
        break;
    }
  };

  const handleDismiss = useCallback((): void => {
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleChange = useCallback(
    (option: SelectFieldOptionConfig): void => {
      field.onChange(option.value);
      onChange(option.value);
      setTimeout(handleDismiss, 200);
    },
    [field, handleDismiss, onChange]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent): void => {
      if (
        isOpen &&
        selectFieldRef.current &&
        !selectFieldRef.current.contains(event.target as Node)
      ) {
        handleDismiss();
      }
    },
    [isOpen, handleDismiss]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <Box ref={selectFieldRef} width="100%">
      <Flex flexDirection="column" justifyContent="center">
        <PrimaryLabel
          id={primaryLabelId}
          required={required}
          primaryLabel={primaryLabel}
          webInputId={inputId}
          primaryLabelA11yHidden={primaryLabelA11yHidden}
          primaryLabelSize={primaryLabelSize}
          requirednessLabel={requirednessLabel}
        />
        <SecondaryLabel secondaryLabel={secondaryLabel} labelId={secondaryLabelId} />
      </Flex>
      <SelectFieldInput
        id={inputId}
        menuId={menuId}
        primaryLabel={primaryLabel}
        required={required}
        secondaryLabel={secondaryLabel}
        disabled={disabled}
        ref={(r) => {
          inputRef.current = r; // inputRef is for our usages
          // field.ref is for react-hook-form, so it can pass focus to the field on error
          field.ref({
            ...r,
            // The setTimeout here improves the screenreader output by letting the
            // attributes on the field update before focus moves to it
            focus: () => setTimeout(() => r?.focus(), 0),
          });
        }}
        placeholder={placeholder}
        selectedOption={selectedOption}
        isOpen={isOpen}
        fieldError={fieldError}
        fieldErrorId={fieldErrorId}
        toggleOpen={toggleOpen}
        onKeyDown={handleInputKeyDown}
        width={inputWidth}
      />
      <SelectFieldOptions
        id={menuId}
        ref={menuRef}
        options={options}
        selectedOption={selectedOption}
        isOpen={isOpen}
        onChange={handleChange}
        onDismiss={handleDismiss}
        width={variantOptions.dropdownWidth}
      />
      <FeedbackMessage
        fieldError={fieldError}
        fieldErrorId={fieldErrorId}
        helperMessage={helperMessage}
        helperMessageId={helperMessageId}
      />
    </Box>
  );
};

export default SelectField;
